<template>
  <div id="app">
    <router-view/>
    <paper-download-list
      :visible="downloadListVisible"
      :collapsed="downloadListCollapsed"
      :tasks="downloadTasks"
      :summary="downloadTaskSummary"
      :language="language"
      :can-open-task="canOpenDownloadTask"
      @toggle-collapse="downloadListCollapsed = !downloadListCollapsed"
      @close="closeDownloadList"
      @open-task="openDownloadTask"
      @retry-task="retryDownloadTask"
      @retry-bundle-item="retryDownloadBundleItem"
      @remove-task="removeDownloadTask"
    ></paper-download-list>
    <paper-upload-list
      :visible="uploadListVisible"
      :collapsed="uploadListCollapsed"
      :tasks="uploadTasks"
      :summary="uploadTaskSummary"
      :language="language"
      :bottom-offset="uploadListBottomOffset"
      @toggle-collapse="uploadListCollapsed = !uploadListCollapsed"
      @close="closeUploadList"
      @retry-task="retryUploadTask"
      @remove-task="removeUploadTask"
    ></paper-upload-list>
  </div>
</template>

<script>
import PaperDownloadList from '@/components/PaperDownloadList.vue'
import PaperUploadList from '@/components/PaperUploadList.vue'
import downloadCenter from '@/utils/downloadCenter'
import uploadCenter from '@/utils/uploadCenter'
import { getLanguage } from '@/api/auth'

export default {
  components: {
    PaperDownloadList,
    PaperUploadList
  },
  data () {
    return {
      language: getLanguage()
    }
  },
  computed: {
    downloadTasks() {
      return downloadCenter.state.tasks
    },
    downloadListVisible() {
      return downloadCenter.state.visible
    },
    downloadListCollapsed: {
      get() {
        return downloadCenter.state.collapsed
      },
      set(value) {
        downloadCenter.state.collapsed = !!value
      }
    },
    downloadTaskSummary() {
      return downloadCenter.getSummary(this.language)
    },
    uploadTasks() {
      return uploadCenter.state.tasks
    },
    uploadListVisible() {
      return uploadCenter.state.visible
    },
    uploadListCollapsed: {
      get() {
        return uploadCenter.state.collapsed
      },
      set(value) {
        uploadCenter.state.collapsed = !!value
      }
    },
    uploadTaskSummary() {
      return uploadCenter.getSummary(this.language)
    },
    uploadListBottomOffset() {
      if (!this.downloadListVisible || !this.downloadTasks.length) {
        return 24
      }
      return this.downloadListCollapsed ? 92 : 'calc(30vh + 40px)'
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('viwoods-language-change', this.handleLanguageChange)
      window.addEventListener('viwoods-auth-logout', this.handleAuthLogout)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('viwoods-language-change', this.handleLanguageChange)
      window.removeEventListener('viwoods-auth-logout', this.handleAuthLogout)
    }
  },
  methods: {
    handleLanguageChange(event) {
      this.language = event && event.detail && event.detail.language ? event.detail.language : getLanguage()
    },
    handleAuthLogout() {
      downloadCenter.closeList()
      uploadCenter.closeList()
    },
    canOpenDownloadTask(task) {
      return downloadCenter.canOpenTask(task)
    },
    openDownloadTask(task) {
      downloadCenter.openTask(task)
    },
    retryDownloadTask(task) {
      downloadCenter.retryTask(task)
    },
    retryDownloadBundleItem(task, bundleItem) {
      downloadCenter.retryBundleItem(task, bundleItem)
    },
    removeDownloadTask(task) {
      downloadCenter.removeTask(task)
    },
    closeDownloadList() {
      downloadCenter.closeList()
    },
    retryUploadTask(task) {
      uploadCenter.retryTask(task)
    },
    removeUploadTask(task) {
      uploadCenter.removeTask(task)
    },
    closeUploadList() {
      uploadCenter.closeList()
    }
  }
}
</script>

<style>
:root {
  --viwoods-loading-color: #1F3A2D;
}

button,
input[type="button"],
input[type="submit"],
input[type="reset"],
.ant-btn,
.button,
[class*="-button"],
[class*="_button"],
.switch-login,
.send-code,
.open-setting,
.right_block {
  border-radius: 0 !important;
}

.ant-input:hover:not(:focus):not(.ant-input-focused),
.ant-input-affix-wrapper:hover:not(.ant-input-affix-wrapper-focused),
input:hover:not(:focus),
textarea:hover:not(:focus) {
  border-color: #d9d9d9 !important;
}

#app .ant-spin-nested-loading .ant-spin .ant-spin-dot,
#app .ant-spin-spinning .ant-spin-dot {
  color: var(--viwoods-loading-color) !important;
}

#app .ant-spin-nested-loading .ant-spin .ant-spin-dot .ant-spin-dot-item,
#app .ant-spin-spinning .ant-spin-dot .ant-spin-dot-item {
  background-color: var(--viwoods-loading-color) !important;
}

#app .ant-spin-text {
  color: var(--viwoods-loading-color) !important;
}

#app .paper-preview-stage {
  position: relative;
}

#app .paper-preview-page-image {
  transition: opacity .16s ease;
}

#app .paper-preview-page-image.is-loading {
  opacity: 0;
}

#app .paper-preview-page-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  pointer-events: none;
}

#app .paper-preview-page-loading > .paper-preview-page-loading-spin {
  color: var(--viwoods-loading-color) !important;
}

#app .paper-preview-page-loading > .paper-preview-page-loading-spin .ant-spin-dot {
  position: static;
  margin: 0;
}

#app .paper-upload-input {
  display: none !important;
}

#app .list-bottom-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px 0 20px;
  box-sizing: border-box;
  color: #6f6f6f;
  font-size: 13px;
  line-height: 20px;
}

#app .list-bottom-loading .ant-spin {
  color: var(--viwoods-loading-color) !important;
}

#app .cloud-list-spin.ant-spin-nested-loading > div > .ant-spin,
#app .paper-list-spin.ant-spin-nested-loading > div > .ant-spin,
#app .daily-list-spin.ant-spin-nested-loading > div > .ant-spin,
#app .loading-spinner.ant-spin-nested-loading > div > .ant-spin {
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-height: none !important;
  padding-top: 0 !important;
  box-sizing: border-box !important;
  display: block !important;
  color: var(--viwoods-loading-color) !important;
}

#app .cloud-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot,
#app .paper-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot,
#app .daily-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot,
#app .loading-spinner.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot {
  position: absolute !important;
  top: 33.333% !important;
  left: 50% !important;
  margin: -10px 0 0 -10px !important;
}

#app .paper-password-field button span,
#app .recent-password-field button span {
  display: block !important;
  width: 22px !important;
  height: 22px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent url("~@/assets/common/password-eye-hidden.png") center / 22px 22px no-repeat !important;
  transform: none !important;
}

#app .paper-password-field button span::before,
#app .paper-password-field button span::after,
#app .recent-password-field button span::before,
#app .recent-password-field button span::after {
  content: '' !important;
  display: none !important;
}

#app .paper-password-field button span.visible,
#app .recent-password-field button span.visible {
  background-image: url("~@/assets/common/password-eye.png") !important;
}

#app .paper-row-cover .paper-row-cover-default.encrypt-icon,
#app .recent-row-cover .recent-row-cover-default.encrypt-icon,
#app .global-search-row-cover .global-search-row-cover-default.encrypt-icon {
  width: 46px !important;
  height: 62px !important;
  border: 1px solid #d7d9d6 !important;
  background-color: #fff !important;
  box-sizing: border-box !important;
  object-fit: contain !important;
}

#app .recent-cover-default,
#app .recent-row-cover-default {
  width: 46px !important;
  height: 46px !important;
  display: block !important;
  box-sizing: content-box !important;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  background-size: contain !important;
}

#app .paper-row-cover.locked,
#app .recent-row-cover.locked,
#app .global-search-row-cover.locked {
  height: 62px !important;
  overflow: visible !important;
}

#app .paper-row.selected,
#app .recent-row.selected,
#app .global-search-row.selected {
  background: #DDE4D9 !important;
  background-color: #DDE4D9 !important;
}

#app .paper-card,
#app .paper-row,
#app .recent-card,
#app .recent-row,
#app .global-search-card,
#app .global-search-row,
#app .daily-card,
#app .daily-row {
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
}

#app .paper-tool-button,
#app .daily-preview-tool,
#app .learning-annotation-preview-tool,
#app .recent-learning-preview-tool {
  position: relative !important;
  overflow: visible !important;
}

#app .preview-zoom-tip {
  position: absolute;
  top: calc(100% + 13px);
  left: 50%;
  z-index: 200;
  min-width: 118px;
  height: 45px;
  padding: 0 18px;
  box-sizing: border-box;
  transform: translateX(-50%);
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #202428;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  line-height: 17px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, .04);
}

#app .paper-tool-button:disabled,
#app .daily-preview-tool:disabled,
#app .learning-annotation-preview-tool:disabled,
#app .recent-learning-preview-tool:disabled {
  opacity: 1 !important;
}

#app .paper-tool-button:disabled > img,
#app .daily-preview-tool:disabled > img,
#app .learning-annotation-preview-tool:disabled > img,
#app .recent-learning-preview-tool:disabled > img {
  opacity: .35 !important;
}

#app .paper-filter,
#app .paper-sort,
#app .daily-filter-wrap,
#app .global-search-filter {
  flex: 0 0 auto !important;
  max-width: calc(100vw - 72px) !important;
}

#app .paper-filter-button,
#app .paper-sort-button,
#app .daily-filter-button,
#app .global-search-filter-button {
  width: auto !important;
  min-width: 118px !important;
  max-width: calc(100vw - 72px) !important;
  white-space: nowrap !important;
}

#app .paper-filter-button span:not(.filter-arrow),
#app .paper-sort-button,
#app .daily-filter-button em,
#app .global-search-filter-button span:not(.global-search-filter-arrow) {
  flex: 0 1 auto !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}

#app .paper-filter-menu,
#app .paper-sort-menu,
#app .daily-filter-menu,
#app .global-search-filter-menu {
  width: max-content !important;
  min-width: 100% !important;
  max-width: calc(100vw - 72px) !important;
}

#app .paper-filter-menu button,
#app .paper-sort-menu button,
#app .daily-filter-menu button,
#app .global-search-filter-menu button {
  width: max-content !important;
  min-width: 100% !important;
  white-space: nowrap !important;
}

#app .paper-filter-menu button span,
#app .paper-sort-menu button span,
#app .daily-filter-menu button span,
#app .global-search-filter-menu button span {
  flex: 0 1 auto !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}

body .cloud-setting-modal .ant-spin-spinning .ant-spin-dot,
body .cloud-setting-modal .ant-spin-nested-loading .ant-spin .ant-spin-dot {
  color: var(--viwoods-loading-color) !important;
}

body .cloud-setting-modal .ant-spin-spinning .ant-spin-dot .ant-spin-dot-item,
body .cloud-setting-modal .ant-spin-nested-loading .ant-spin .ant-spin-dot .ant-spin-dot-item {
  background-color: var(--viwoods-loading-color) !important;
}

html body .custom-loading .el-loading-spinner .path,
html body .el-loading-spinner .path {
  stroke: var(--viwoods-loading-color) !important;
}

html body .custom-loading .el-loading-spinner .circular,
html body .el-loading-spinner .circular {
  color: var(--viwoods-loading-color) !important;
}

html body .custom-loading .el-loading-spinner > div,
html body .el-loading-spinner > div {
  border-color: var(--viwoods-loading-color) transparent transparent transparent !important;
}

html body .custom-loading .el-loading-text,
html body .el-loading-text,
html body .el-icon-loading {
  color: var(--viwoods-loading-color) !important;
}

.is-interaction-locked {
  position: relative;
}

.is-interaction-locked::after {
  content: '';
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: transparent;
  cursor: wait;
  pointer-events: auto;
  touch-action: none;
}
</style>
